import { ClassType, EntityType, Rights } from '@frontend/common';
import { ObjectListLayoutProvider, defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConfirmationModal } from '@frontend/components/modal';
import { ErrorHandler } from '@frontend/error-handler';
import { UserGroupClient } from '@frontend/group/api';
import { GroupMember, IGroup } from '@frontend/group/types';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { refresh, resetSelect } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

interface UserGroupMembersTabProps {
    group: IGroup;
}

const MEMBER_LIST_ID = 'user-group-detail-modal-member-list';
const ADD_MEMBER_LIST_ID = 'user-group-detail-modal-add-member-list';
const ADD_MEMBERS_MODAL_ID = 'user-group-detail-modal-add-members-modal';
const DELETE_MEMBERS_MODAL_ID = 'user-group-detail-modal-delete-members-modal';
const CONFIRM_ADD_MEMBERS_MODAL_ID = 'user-group-detail-modal-confirm-add-members-modal';
const MEMBERS_LABEL = (
    <FormattedMessage
        id='user-group-detail-modal.members.label'
        description='Tab label for the members section in the user group detail modal'
        defaultMessage='Members'
    />
);

export const UserGroupMembersTab = (props: UserGroupMembersTabProps) => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const { openModal, closeModal } = useModals();
    const selectedMembers = useAppSelector((state) => (state.lists[MEMBER_LIST_ID]?.selected ?? []) as GroupMember[]);

    const refreshMembers = () => {
        dispatch(refresh({ listId: MEMBER_LIST_ID, refresh: true }));
        dispatch(resetSelect({ listId: MEMBER_LIST_ID }));
    };

    const openAddMembersModal = () => {
        openModal(
            ADD_MEMBERS_MODAL_ID,
            () => (
                <AddUserGroupMembersModal
                    group={props.group}
                    onSuccess={() => {
                        closeModal(ADD_MEMBERS_MODAL_ID);
                        refreshMembers();
                    }}
                />
            ),
            { handleCloseCallback: () => dispatch(resetSelect({ listId: ADD_MEMBER_LIST_ID })) },
            { className: 'max-w-[1000px]! w-[1000px]!' }
        );
    };

    const deleteSelectedMembers = () => {
        UserGroupClient.deleteUserGroupMembers(selectedMembers.map((member) => member.id))
            .then(() => {
                ToastUtil.info(
                    intl.formatMessage(
                        {
                            id: 'feature.user-groups.members.deleted-multiple-success',
                            defaultMessage: 'Successfully removed {count} members from user group {groupName}.'
                        },
                        {
                            count: selectedMembers.length,
                            groupName: props.group.name
                        }
                    )
                );
                refreshMembers();
            })
            .catch(ErrorHandler.handleError)
            .finally(() => closeModal(DELETE_MEMBERS_MODAL_ID));
    };

    return (
        <ObjectListLayoutProvider layout='admin'>
            <ReactiveObjectList<GroupMember>
                id={MEMBER_LIST_ID}
                useStaticListId
                type={EntityType.MEMBER}
                fetch={(arg) => UserGroupClient.fetchUserGroupMembers({ group_id: props.group.id, ...arg })}
                dispatch={dispatch}
                eventListener={EntityType.GROUP}
                columns={{
                    columns: ['entity_id'],
                    headerOverwrite: new Map([['entity_id', CommonMessage.OBJECTS.USER.DEFAULT]]),
                    customCells: new Map([
                        [
                            'entity_id',
                            (o) => (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        entityType={EntityType.USER}
                                        id={o.row.original.entity_id}
                                    />
                                </td>
                            )
                        ]
                    ])
                }}
                multiSelectOptions={{
                    custom: [
                        {
                            text: CommonMessage.BUTTONS.ADD(),
                            onClick: openAddMembersModal,
                            type: ClassType.PRIMARY,
                            icon: ICONS.BUTTON.CREATE,
                            requiredPermissions: [
                                { item: EntityType.USER_GROUP, source: EntityType.USER_GROUP, right: Rights.UPDATE, uid: props.group.id },
                                { item: EntityType.USER_GROUP, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: props.group.account_id }
                            ]
                        },
                        {
                            id: 'delete-button',
                            text: CommonMessage.BUTTONS.DELETE,
                            enabled: (members) => members.length > 0,
                            type: ClassType.DANGER,
                            icon: ICONS.BUTTON.DELETE,
                            requiredPermissions: [
                                { item: EntityType.USER_GROUP, source: EntityType.USER_GROUP, right: Rights.UPDATE, uid: props.group.id },
                                { item: EntityType.USER_GROUP, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: props.group.account_id }
                            ],
                            onClick: () =>
                                openModal(DELETE_MEMBERS_MODAL_ID, () => (
                                    <ConfirmationModal
                                        handleClose={() => closeModal(DELETE_MEMBERS_MODAL_ID)}
                                        message={
                                            <FormattedMessage
                                                id='user-group-detail-modal.members.delete-confirmation'
                                                description='Confirmation message shown before removing selected users from a user group in the user group detail modal'
                                                defaultMessage='Are you sure you want to remove {count} users from this group?'
                                                values={{ count: selectedMembers.length }}
                                            />
                                        }
                                        onConfirm={deleteSelectedMembers}
                                        severity={ClassType.DANGER}
                                        show={true}
                                    />
                                ))
                        }
                    ]
                }}
                filter={{ default: true, queryParamKeyOverwrite: new Map([['search', 'user']]) }}
                showCount
            />
        </ObjectListLayoutProvider>
    );
};

interface AddUserGroupMembersModalProps {
    group: IGroup;
    onSuccess: () => void;
}

const AddUserGroupMembersModal = (props: AddUserGroupMembersModalProps) => {
    const dispatch = useAppDispatch();
    const { openModal, closeModal } = useModals();
    const selectedUsers = useAppSelector((state) => (state.lists[ADD_MEMBER_LIST_ID]?.selected ?? []) as IUser[]);

    const addSelectedMembers = () => {
        UserGroupClient.postUserGroupMemberList(
            props.group.account_id,
            props.group.id,
            EntityType.USER,
            selectedUsers.map((user) => user.id)
        )
            .then(() => {
                dispatch(resetSelect({ listId: ADD_MEMBER_LIST_ID }));
                closeModal(CONFIRM_ADD_MEMBERS_MODAL_ID);
                props.onSuccess();
            })
            .catch(ErrorHandler.handleError);
    };

    return (
        <div className='modal bg-background-default flex h-full flex-col gap-6 p-6'>
            <div className='modal-header flex items-start justify-between px-0 py-0'>
                <div className='text-foreground-default text-3xl font-semibold'>{CommonMessage.BUTTONS.ADD(MEMBERS_LABEL)}</div>
                <button
                    type='button'
                    onClick={() => closeModal(ADD_MEMBERS_MODAL_ID)}
                    className='bg-background-surface hover:bg-brand-light border-border-default text-brand-muted size-11 cursor-pointer rounded-md border p-2.5'>
                    <ICONS.BUTTON.CLOSE_2 className='size-full' />
                </button>
            </div>
            <ObjectListLayoutProvider layout='admin'>
                <ReactiveObjectList<IUser>
                    id={ADD_MEMBER_LIST_ID}
                    useStaticListId
                    type={EntityType.USER}
                    dispatch={dispatch}
                    fetch={(params) => UserClient.fetchUsersNotInGroup(props.group.account_id, props.group.id, params)}
                    columns={{
                        columns: ['first_name', 'last_name']
                    }}
                    multiSelectOptions={{
                        custom: [
                            {
                                id: 'add-button',
                                text: CommonMessage.BUTTONS.ADD(),
                                enabled: (users) => users.length > 0,
                                type: ClassType.SUCCESS,
                                icon: ICONS.BUTTON.CREATE,
                                requiredPermissions: [
                                    { item: EntityType.USER_GROUP, source: EntityType.USER_GROUP, right: Rights.UPDATE, uid: props.group.id },
                                    { item: EntityType.USER_GROUP, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: props.group.account_id }
                                ],
                                onClick: () =>
                                    openModal(CONFIRM_ADD_MEMBERS_MODAL_ID, () => (
                                        <ConfirmationModal
                                            handleClose={() => closeModal(CONFIRM_ADD_MEMBERS_MODAL_ID)}
                                            message={
                                                <FormattedMessage
                                                    id='user-group-detail-modal.members.add-confirmation'
                                                    description='Confirmation message shown before adding selected users to a user group in the user group detail modal'
                                                    defaultMessage='Are you sure you want to add {count} users to this group?'
                                                    values={{ count: selectedUsers.length }}
                                                />
                                            }
                                            onConfirm={addSelectedMembers}
                                            severity={ClassType.PRIMARY}
                                            show={true}
                                        />
                                    ))
                            }
                        ]
                    }}
                    filter={{ default: true }}
                    showCount
                />
            </ObjectListLayoutProvider>
        </div>
    );
};
